import { Link } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { EmptyState, WhereaboutsModal } from "shared/components/root";
import { useConnectToListingModal } from "./useConnectToListingModal";
import { Input, Radio, RadioGroup, Text } from "@ttc3k/trekker";
import { SimpleErrorNotification, SimpleLoader } from "shared/components/atoms";
import { Flex } from "styled-system/jsx";
import { SimpleSelectableCard } from "shared/components/molecules";
import { getAddressString, getMultilingualString } from "shared/utils";
import { OperatorsManyPersonConnectToListingQuery } from "gql/generated";

type OperatorListingCardProps = {
  operator: OperatorsManyPersonConnectToListingQuery["operatorMany"][0];
  isSelected: boolean;
  onOperatorSelected: (
    operator: OperatorsManyPersonConnectToListingQuery["operatorMany"][0],
  ) => void;
};

/**
 * Common card so all of the prop overrides aren't duplicated
 * @returns
 */
const OperatorListingCard = ({
  operator,
  isSelected,
  onOperatorSelected,
}: OperatorListingCardProps) => {
  return (
    <SimpleSelectableCard
      display="flex"
      flexDir="column"
      justifyContent="center"
      alignItems="flex-start"
      gap="100"
      bg="white"
      p="300 !important"
      minH="104px"
      label={getMultilingualString(operator.name ?? {})}
      labelProps={{
        visual: "bodySemiBold",
      }}
      description={getAddressString(
        operator.contact?.address ?? {},
        false,
        false,
      )}
      descriptionProps={{
        visual: "smallMedium",
        color: "text.light",
      }}
      selected={isSelected}
      onClick={() => onOperatorSelected(operator)}
    />
  );
};

type ConnectToListingModalProps = {
  isOpen: boolean;
  personId: string;
  onClose: () => void;
  // Currently, this does the same thing as onClose,
  // but we may want to do something different in the future
  onConnectSuccess: () => void;
};

export const ConnectToListingModal = ({
  isOpen,
  personId,
  onClose,
  onConnectSuccess,
}: ConnectToListingModalProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_DETAILS_MODAL.LISTINGS.CONNECT_MODAL",
  });

  const {
    operators,
    operatorsLoading,
    operatorsError,
    filteredOperators,
    selectedOperator,
    isSelectedOperatorInFilteredOperators,
    isConnecting,
    sendInvitationEmail,
    setSendInvitationEmail,
    handleSearch,
    handleConnectToListing,
    handleSelectOperator,
    handleCloseClick,
  } = useConnectToListingModal({ personId, onClose, onConnectSuccess });

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={handleCloseClick}
      hideCloseTrigger={isConnecting}
      title={t("TITLE")}
      description={t("DESCRIPTION")}
      HeaderIcon={Link}
      cancelButtonProps={{
        onClick: handleCloseClick,
      }}
      saveButtonCopy={t("BUTTONS.CONNECT")}
      saveButtonProps={{
        visual: "accent.blue",
        isLoading: isConnecting,
        disabled: !selectedOperator,
        onClick: handleConnectToListing,
      }}
      portal
    >
      {operatorsLoading && <SimpleLoader />}
      {operatorsError && <SimpleErrorNotification />}
      {operators.length > 0 && (
        <Flex flexDir="column" gap="200">
          {/* Search */}
          <Input
            name="connect-to-listing-search"
            label={t("LISTING_NAME")}
            // value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder={t("LISTING_NAME_PLACEHOLDER")}
          />

          {/* Listings */}
          <Flex
            flexDir="column"
            gap="200"
            h="204px"
            mt="-25" // Move the top up by 2px so the selected outline is visible
            mx="-25" // Move the left and right by 2px so the selected outline is visible
            pt="25" // Add padding to offset the margin adjustment
            px="25" // Add padding to offset the margin adjustment
            overflowY="auto"
          >
            {/* NO LISTINGS */}
            {filteredOperators.length === 0 && (
              <EmptyState text={t("NO_LISTINGS")} />
            )}

            {/* SELECTED LISTING */}
            {selectedOperator && !isSelectedOperatorInFilteredOperators && (
              <OperatorListingCard
                operator={selectedOperator}
                isSelected={true}
                onOperatorSelected={handleSelectOperator}
              />
            )}
            {filteredOperators.map(operator => (
              <OperatorListingCard
                key={operator._entityId}
                operator={operator}
                isSelected={operator._entityId === selectedOperator?._entityId}
                onOperatorSelected={handleSelectOperator}
              />
            ))}
          </Flex>

          {/* Invitations */}
          <Flex flexDir="column">
            <Text visual="bodySemiBold" mb="100">
              {t("INVITATIONS.TITLE")}
            </Text>
            <Text visual="bodyRegular" color="text.mid" mb="200">
              {t("INVITATIONS.DESCRIPTION")}
            </Text>

            {/* Since the Trekker RadioGroup and Radio buttons can only have string values, we need to convert the boolean to a string */}
            <RadioGroup
              value={sendInvitationEmail}
              onValueChange={({ value }: { value: "yes" | "no" }) => {
                setSendInvitationEmail(value);
              }}
              orientation="horizontal"
            >
              <Radio value="no">
                {t("INVITATIONS.DO_NOT_SEND_INVITATION_EMAIL")}
              </Radio>
              <Radio value="yes">
                {t("INVITATIONS.SEND_INVITATION_EMAIL")}
              </Radio>
            </RadioGroup>
          </Flex>
        </Flex>
      )}
    </WhereaboutsModal>
  );
};
